import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { getWeekDates } from "../shared/dateUtils";
import { useIsMobile } from "../shared/utils";
import SiteAccessPanel from "./SiteAccessPanel";
import {
  ServiceRequestWorkModal,
  RectificationWorkModal,
} from "./FieldTechWorkModal";

// Jobs arrive in two shapes: an inspection's full datetime, and a service
// request's bare "YYYY-MM-DD". A bare date parsed by `new Date()` is UTC
// midnight, which reads as the previous day here — so anchor it to local noon
// instead. SiteAccessPanel does the same thing for the same reason.
function jobDate(value) {
  if (!value) return null;
  return String(value).includes("T")
    ? new Date(value)
    : new Date(value + "T12:00:00");
}

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getMonthGrid(monthOffset) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + monthOffset;
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const grid = [];
  for (let i = 0; i < startDow; i++) grid.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) {
    grid.push(new Date(year, month, d));
  }
  while (grid.length % 7 !== 0) grid.push(null);
  return { grid, year, month };
}

function isToday(date) {
  return formatDate(date) === formatDate(new Date());
}

function statusColor(status) {
  if (status === "passed" || status === "completed") return colors.green;
  if (status === "failed") return colors.red;
  if (status === "pending") return colors.amber;
  return colors.teal;
}

function statusLabel(status) {
  if (status === "passed") return "Passed";
  if (status === "failed") return "Failed";
  if (status === "completed") return "Completed";
  if (status === "pending") return "Pending";
  return "In Progress";
}

function typeLabel(type) {
  if (type === "monthly") return "Monthly";
  if (type === "bi_annual") return "Bi-Annual";
  if (type === "annual") return "Annual";
  return type;
}

export function FieldTechSchedule() {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState("day");
  const [weekOffset, setWeekOffset] = useState(0);
  const [monthOffset, setMonthOffset] = useState(0);
  const [inspections, setInspections] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [rectRequests, setRectRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [workJob, setWorkJob] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const inspRes = await axios.get(`${API}/inspections/`);
      setInspections(inspRes.data);
      // Service requests — may 403 if not permitted yet, handle gracefully
      try {
        const srRes = await axios.get(`${API}/service-requests/`);
        setServiceRequests(srRes.data);
      } catch {
        setServiceRequests([]);
      }
      try {
        const rrRes = await axios.get(`${API}/rectification-requests/`);
        setRectRequests(rrRes.data);
      } catch {
        setRectRequests([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Flexible jobs — no specific date, appear in "Do Anytime" section
  const flexibleJobs = [
    ...inspections
      .filter((i) => i.flexible)
      .map((i) => ({
        id: `insp-${i.id}`,
        type: "inspection",
        date: i.date,
        site_name: i.site_name,
        site_address: i.site_address,
        status: i.status,
        detail: typeLabel(i.inspection_type),
        raw: i,
      })),
    ...serviceRequests
      // Undated counts as anytime. Without this an assigned request with no
      // date yet matches neither bucket and is invisible on this screen.
      .filter((sr) => sr.flexible || !sr.scheduled_date)
      .map((sr) => ({
        id: `sr-${sr.id}`,
        type: "service_request",
        date: sr.scheduled_date,
        time: sr.scheduled_time,
        site_name: sr.site_name || sr.site?.site_name || "—",
        site_address: sr.site_address || sr.site?.site_address || "—",
        status: sr.status,
        detail: (sr.targets || [])[0]?.description || "Service Request",
        raw: sr,
      })),
    ...rectRequests
      .filter((rr) => rr.status !== "completed")
      .map((rr) => ({
        id: `rr-${rr.id}`,
        type: "rectification",
        date: null,
        site_name: rr.quote_detail?.inspection_site_name || "—",
        site_address: rr.quote_detail?.inspection_site_address || "—",
        site: rr.quote_detail?.inspection_site_id,
        status: rr.status.toLowerCase(),
        detail: `${(rr.quote_detail?.items || []).length} defect(s) to rectify`,
        raw: rr,
      })),
  ];

  // Scheduled jobs — non-flexible, with a specific date
  const allJobs = [
    ...inspections
      .filter((i) => !i.flexible)
      .map((i) => ({
        id: `insp-${i.id}`,
        type: "inspection",
        date: i.date,
        site_name: i.site_name,
        site_address: i.site_address,
        status: i.status,
        detail: typeLabel(i.inspection_type),
        raw: i,
      })),
    ...serviceRequests
      .filter((sr) => !sr.flexible && sr.scheduled_date)
      .map((sr) => ({
        id: `sr-${sr.id}`,
        type: "service_request",
        date: sr.scheduled_date,
        time: sr.scheduled_time,
        site_name: sr.site_name || sr.site?.site_name || "—",
        site_address: sr.site_address || sr.site?.site_address || "—",
        status: sr.status,
        detail: (sr.targets || [])[0]?.description || "Service Request",
        raw: sr,
      })),
  ].sort((a, b) => jobDate(a.date) - jobDate(b.date));

  const jobsForDate = (dateStr) =>
    allJobs.filter((j) => j.date && formatDate(jobDate(j.date)) === dateStr);

  const today = new Date();
  const todayStr = formatDate(today);
  const todayJobs = jobsForDate(todayStr);

  const weekDates = getWeekDates(weekOffset);
  const { grid, year, month } = getMonthGrid(monthOffset);
  const monthName = new Date(year, month, 1).toLocaleDateString("en-AU", {
    month: "long",
    year: "numeric",
  });
  const weekLabel =
    weekOffset === 0
      ? "This Week"
      : weekOffset === 1
        ? "Next Week"
        : `+${weekOffset} weeks`;

  const jobPill = (job, compact = false) => {
    const pillBg =
      job.type === "inspection"
        ? "#e8f8f5"
        : job.type === "rectification"
          ? "#fdecea"
          : "#fff8e8";
    const pillCol =
      job.type === "inspection"
        ? colors.teal
        : job.type === "rectification"
          ? colors.red
          : colors.amber;
    const pillIcon =
      job.type === "inspection"
        ? "📋"
        : job.type === "rectification"
          ? "🔨"
          : "🔧";
    return (
      <div
        key={job.id}
        onClick={() => setSelectedJob(job)}
        style={{
          backgroundColor: pillBg,
          border: `1px solid ${pillCol}`,
          borderRadius: "6px",
          padding: compact ? "3px 5px" : "8px 10px",
          marginBottom: compact ? "2px" : "8px",
          cursor: "pointer",
          fontSize: compact ? "10px" : "13px",
        }}
      >
        <div
          style={{
            fontWeight: "600",
            color: pillCol,
            whiteSpace: compact ? "nowrap" : "normal",
            overflow: compact ? "hidden" : "visible",
            textOverflow: compact ? "ellipsis" : "unset",
          }}
        >
          {compact ? job.site_name : `${pillIcon} ${job.site_name}`}
        </div>
        {!compact && (
          <div
            style={{ fontSize: "12px", color: colors.slate, marginTop: "2px" }}
          >
            {job.site_address}
          </div>
        )}
        {!compact && (
          <div
            style={{ fontSize: "11px", color: colors.slate, marginTop: "4px" }}
          >
            {job.detail} ·{" "}
            <span style={{ color: statusColor(job.status), fontWeight: "600" }}>
              {statusLabel(job.status)}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "12px" : "24px",
        maxWidth: "700px",
        height: "100vh",
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          fontSize: "18px",
          fontWeight: "700",
          color: colors.darkGrey,
          marginBottom: "12px",
        }}
      >
        Service Schedule
      </h1>

      {/* View toggle */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        {["day", "week", "month"].map((mode) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            style={{
              padding: "7px 14px",
              borderRadius: "20px",
              border: "none",
              backgroundColor:
                viewMode === mode ? colors.teal : colors.lightGrey,
              color: viewMode === mode ? "white" : colors.slate,
              fontWeight: "600",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            {mode === "day" ? "Today" : mode === "week" ? "Week" : "Month"}
          </button>
        ))}
      </div>

      {/* DAY VIEW */}
      {viewMode === "day" && (
        <div>
          <div
            style={{
              backgroundColor: colors.white,
              borderRadius: "10px",
              padding: "12px 16px",
              marginBottom: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
            }}
          >
            <div
              style={{
                fontWeight: "700",
                fontSize: "15px",
                color: colors.darkGrey,
              }}
            >
              Today —{" "}
              {today.toLocaleDateString("en-AU", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: colors.slate,
                marginTop: "2px",
              }}
            >
              {todayJobs.length} job{todayJobs.length !== 1 ? "s" : ""}{" "}
              scheduled
            </div>
          </div>

          {todayJobs.length === 0 ? (
            <div
              style={{
                backgroundColor: colors.white,
                borderRadius: "10px",
                padding: "40px",
                textAlign: "center",
                color: "#999",
                fontSize: "14px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
              }}
            >
              No jobs scheduled for today 🎉
            </div>
          ) : (
            todayJobs.map((job) => jobPill(job, false))
          )}
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode === "week" && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: colors.white,
              borderRadius: "10px",
              padding: "10px 14px",
              marginBottom: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
            }}
          >
            <button
              onClick={() => setWeekOffset((w) => Math.max(-4, w - 1))}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: colors.teal,
              }}
            >
              ‹
            </button>
            <div
              style={{
                fontWeight: "700",
                fontSize: "14px",
                color: colors.darkGrey,
              }}
            >
              {weekLabel}
            </div>
            <button
              onClick={() => setWeekOffset((w) => Math.min(25, w + 1))}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: colors.teal,
              }}
            >
              ›
            </button>
          </div>

          {weekDates.map((date) => {
            const dateStr = formatDate(date);
            const jobs = jobsForDate(dateStr);
            const today = isToday(date);

            return (
              <div
                key={dateStr}
                style={{
                  backgroundColor: colors.white,
                  borderRadius: "8px",
                  padding: "12px 14px",
                  marginBottom: "8px",
                  border: `2px solid ${today ? colors.teal : jobs.length > 0 ? "#b2dfdb" : colors.border}`,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: jobs.length > 0 ? "8px" : 0,
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: today ? colors.teal : colors.lightGrey,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "9px",
                        fontWeight: "600",
                        color: today ? "white" : colors.slate,
                        lineHeight: 1,
                      }}
                    >
                      {date
                        .toLocaleDateString("en-AU", { weekday: "short" })
                        .toUpperCase()}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: today ? "white" : colors.darkGrey,
                        lineHeight: 1.2,
                      }}
                    >
                      {date.getDate()}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: jobs.length > 0 ? colors.darkGrey : "#aaa",
                      fontWeight: jobs.length > 0 ? "600" : "400",
                    }}
                  >
                    {jobs.length === 0
                      ? "No jobs"
                      : `${jobs.length} job${jobs.length !== 1 ? "s" : ""}`}
                  </div>
                </div>
                {jobs.map((job) => jobPill(job, false))}
              </div>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === "month" && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: colors.white,
              borderRadius: "10px",
              padding: "10px 14px",
              marginBottom: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
            }}
          >
            <button
              onClick={() => setMonthOffset((m) => Math.max(-3, m - 1))}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: colors.teal,
              }}
            >
              ‹
            </button>
            <div
              style={{
                fontWeight: "700",
                fontSize: "14px",
                color: colors.darkGrey,
              }}
            >
              {monthName}
            </div>
            <button
              onClick={() => setMonthOffset((m) => Math.min(11, m + 1))}
              style={{
                background: "none",
                border: "none",
                fontSize: "22px",
                cursor: "pointer",
                color: colors.teal,
              }}
            >
              ›
            </button>
          </div>

          <div
            style={{
              backgroundColor: colors.white,
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
            }}
          >
            {/* Day headers */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                backgroundColor: colors.darkGrey,
              }}
            >
              {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                <div
                  key={i}
                  style={{
                    textAlign: "center",
                    padding: "8px 0",
                    fontSize: "11px",
                    fontWeight: "700",
                    color:
                      i >= 5
                        ? "rgba(255,255,255,0.3)"
                        : "rgba(255,255,255,0.7)",
                  }}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Cells */}
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}
            >
              {grid.map((date, idx) => {
                if (!date)
                  return (
                    <div
                      key={idx}
                      style={{
                        minHeight: "60px",
                        backgroundColor: "#fafafa",
                        borderRight: `1px solid ${colors.border}`,
                        borderBottom: `1px solid ${colors.border}`,
                      }}
                    />
                  );

                const dateStr = formatDate(date);
                const jobs = jobsForDate(dateStr);
                const today = isToday(date);
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                return (
                  <div
                    key={idx}
                    style={{
                      minHeight: "60px",
                      padding: "4px",
                      backgroundColor: isWeekend
                        ? "#fafafa"
                        : jobs.length > 0
                          ? "#f0faf7"
                          : colors.white,
                      borderRight: `1px solid ${colors.border}`,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    <div
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        backgroundColor: today ? colors.teal : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 2px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: today ? "700" : "400",
                          color: today
                            ? "white"
                            : isWeekend
                              ? "#ccc"
                              : colors.darkGrey,
                        }}
                      >
                        {date.getDate()}
                      </span>
                    </div>
                    {jobs.slice(0, 2).map((job) => jobPill(job, true))}
                    {jobs.length > 2 && (
                      <div
                        style={{
                          fontSize: "9px",
                          color: colors.slate,
                          textAlign: "center",
                        }}
                      >
                        +{jobs.length - 2} more
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div style={{ padding: "10px 0", display: "flex", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "2px",
                  backgroundColor: "#e8f8f5",
                  border: `1px solid ${colors.teal}`,
                }}
              />
              <span style={{ fontSize: "11px", color: colors.slate }}>
                Inspection
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "2px",
                  backgroundColor: "#fff8e8",
                  border: `1px solid ${colors.amber}`,
                }}
              />
              <span style={{ fontSize: "11px", color: colors.slate }}>
                Service Request
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Do Anytime section — shown in all views */}
      {flexibleJobs.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: colors.amber,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "8px",
            }}
          >
            Do Anytime ({flexibleJobs.length})
          </div>
          {flexibleJobs.map((job) => jobPill(job, false))}
        </div>
      )}

      <SiteAccessPanel
        job={selectedJob}
        onClose={() => setSelectedJob(null)}
        onStartWork={setWorkJob}
      />

      {workJob?.type === "service_request" && (
        <ServiceRequestWorkModal
          job={workJob}
          onClose={() => setWorkJob(null)}
          onSaved={async () => {
            try {
              const res = await axios.get(`${API}/service-requests/`);
              setServiceRequests(res.data);
            } catch {}
          }}
        />
      )}
      {workJob?.type === "rectification" && (
        <RectificationWorkModal
          job={workJob}
          onClose={() => setWorkJob(null)}
          onSaved={async () => {
            try {
              const res = await axios.get(`${API}/rectification-requests/`);
              setRectRequests(res.data);
            } catch {}
          }}
        />
      )}

      <div style={{ height: "40px" }} />
    </div>
  );
}
